import MonsterSchemaUtils from './MonsterSchema';
import monsterDataLoader from './MonsterDataLoader';
import { CharacterDataService, characterTemplates as fullCharacterTemplates } from './CharacterDataService';
import { DEBUG_ENABLED } from '../utils/DebugLogger';

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

/**
 * DataService - Real D&D 5e Data Integration for BattleRoom
 * 
 * This service provides real Dungeons & Dragons 5th Edition character and monster data
 * to replace mock data with authentic game statistics and abilities.
 * 
 * WHAT IT PROVIDES:
 * - Pre-built character templates (Fighter, Wizard) with accurate D&D 5e stats
 * - Monster templates (Goblin, Orc) with official challenge ratings and abilities
 * - Spell database with descriptions and game mechanics
 * - Utility functions for creating characters/monsters from templates
 * - Dice rolling functions for damage and initiative
 * 
 * HOW TO USE:
 * - DataService.createCharacterFromTemplate('fighter') creates a new Fighter
 * - DataService.createMonsterFromTemplate('goblin') creates a new Goblin
 * - DataService.rollInitiative(character) rolls initiative with proper modifiers
 * - DataService.rollDamage('1d8+2') rolls damage dice with modifiers
 * 
 * INTEGRATION:
 * - Used by Dashboard.js to populate character/monster lists
 * - Used by BattleRoom.js for creating tokens with real stats
 * - Used by Initiative Tracker for authentic combat mechanics
 * - Designed to be easily expanded with more character classes and monsters
 */

// @date_of_change 2025-08-30
// Legacy character templates - now replaced by full character data from CharacterDataService
// These are kept for backward compatibility but new features should use CharacterDataService
export const characterTemplates = fullCharacterTemplates;

// date_of_change: 2025-10-18 - DEPRECATED inline monsterTemplates
// rule_reference: AGENTS.md SSOT — Use MonsterDataLoader/UDM exclusively for monsters

// Spell database
export const spellDatabase = {
  "Magic Missile": {
    name: "Magic Missile",
    level: 1,
    school: "evocation",
    castingTime: "1 action",
    range: "120 feet",
    components: "V, S",
    duration: "Instantaneous",
    description: "You create three glowing darts of magical force. Each dart hits a creature of your choice that you can see within range. A dart deals 1d4 + 1 force damage to its target."
  },
  
  "Shield": {
    name: "Shield",
    level: 1,
    school: "abjuration",
    castingTime: "1 reaction",
    range: "Self",
    components: "V, S",
    duration: "1 round",
    description: "An invisible barrier of magical force appears and protects you. Until the start of your next turn, you have a +5 bonus to AC."
  }
};

// Data service functions
export class DataService {
  
  /**
   * Convert abilities object with long names to stats object with short names
   * date_of_change: 2025-09-13 - Architectural enforcement: forbidden transformation (see ArchitecturalCompliance.test.js)
   */
  static convertAbilitiesToStats() {
    // rule_reference: ArchitecturalCompliance Forbidden DataService Methods
    // Rationale: Handlers and canonical schemas must be used; UI extracts legacy views locally if needed
    throw new Error('ARCHITECTURAL VIOLATION: Use discrete handlers from CanonicalSchemas.ts');
  }
  
  /**
   * Convert comprehensive equipment structure to simple array for CharacterSheet
   * date_of_change: 2025-09-13 - Architectural enforcement: forbidden transformation (see ArchitecturalCompliance.test.js)
   */
  static convertEquipmentToArray() {
    // rule_reference: ArchitecturalCompliance Forbidden DataService Methods
    // Rationale: Equipment structure must remain canonical; projection handled by handlers or UI-only mapping
    throw new Error('ARCHITECTURAL VIOLATION: Use discrete handlers from CanonicalSchemas.ts');
  }
  
  // @date_of_change 2025-08-30
  // Enhanced to use CharacterDataService for complete character sheets with spells and features
  static createCharacterFromTemplate(templateName, customizations = {}) {
    // First check if this is a custom character from CustomAssetManager
    if (templateName && templateName.startsWith('custom_character_')) {
      try {
        // Import here to avoid circular dependency
        const CustomAssetManager = require('./CustomAssetManager').default;
        const customCharacter = CustomAssetManager.getAssetById(templateName);
        
        if (customCharacter) {
          debugLog(`[DataService] Found custom character template: ${templateName}`, customCharacter.name);
          // @date_of_change 2025-09-12 - CRITICAL FIX: Access canonicalData for characters (following Dashboard pattern)
          // CustomAssetManager loads characters with canonicalData, not customData
          const characterData = customCharacter.canonicalData || customCharacter.customData || {};
          debugLog(`[DataService] Extracting character data:`, {
            hasCanonicalData: !!customCharacter.canonicalData,
            hasCustomData: !!customCharacter.customData,
            dataKeys: Object.keys(characterData),
            characterDataSample: { 
              name: characterData.name, 
              class: characterData.class, 
              level: characterData.level,
              hasClassFeatures: !!characterData.classFeatures?.length,
              hasItems: !!characterData.items?.length,
              hasFeats: !!characterData.feats?.length,
              hasActions: !!characterData.actions
            },
            // @date_of_change 2025-09-12 - CRITICAL DEBUG: Check if rich arrays are actually present
            richDataPresence: {
              classFeatures: characterData.classFeatures ? `PRESENT (${characterData.classFeatures.length} items)` : 'MISSING',
              items: characterData.items ? `PRESENT (${characterData.items.length} items)` : 'MISSING',
              feats: characterData.feats ? `PRESENT (${characterData.feats.length} items)` : 'MISSING', 
              actions: characterData.actions ? 'PRESENT' : 'MISSING'
            },
            // @date_of_change 2025-09-12 - DETAILED INSPECTION: Show actual values
            detailedInspection: {
              classFeaturesSample: characterData.classFeatures?.slice(0,2) || 'MISSING',
              itemsSample: characterData.items?.slice(0,2) || 'MISSING',
              featsSample: characterData.feats?.slice(0,2) || 'MISSING',
              actionsSample: characterData.actions || 'MISSING'
            }
          });
          // Return the custom character data with proper structure
          return {
            id: customCharacter.id,
            name: customCharacter.name,
            ...characterData,
            ...customizations,
            isCustom: true
          };
        }
      } catch (error) {
        debugLog(`[DataService] Error loading custom character: ${error.message}`);
        // Continue to check standard templates
      }
    }
    
    // Try to use CharacterDataService for complete character sheets
    try {
      const fullCharacter = CharacterDataService.createCharacterFromTemplate(templateName, customizations);
      if (fullCharacter) {
        debugLog(`[DataService] Created full character from CharacterDataService: ${fullCharacter.data.name}`);
        
        // Convert to legacy format for backward compatibility with existing BattleRoom code
        const legacyFormat = CharacterDataService.convertToTokenFormat(fullCharacter, customizations);
        debugLog(`[DataService] Converted to legacy format:`, {
          name: legacyFormat.name,
          actions: legacyFormat.actions?.length || 0,
          spells: Object.keys(legacyFormat.spells || {}).length
        });
        
        return {
          ...legacyFormat,
          // Keep the full character data available for enhanced features
          fullCharacterData: fullCharacter,
          // @date_of_change 2025-08-30
          // Also make full character data available as characterData for ActionTracker compatibility
          characterData: fullCharacter,
          createdAt: new Date().toISOString()
        };
      }
    } catch (error) {
      // Fall back to legacy templates
    }
    
    // Fallback to legacy character templates if CharacterDataService fails
    const template = fullCharacterTemplates[templateName];
    if (!template) {
      throw new Error(`Character template '${templateName}' not found`);
    }
    
    return {
      id: `char_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...template,
      ...customizations,
      createdAt: new Date().toISOString()
    };
  }
  
  static createMonsterFromTemplate(templateName, customizations = {}) {
    // date_of_change: 2025-10-18 - Route to MonsterDataLoader (canonical source)
    const monster = monsterDataLoader.getAllMonsters()?.[templateName];
    if (!monster) {
      throw new Error(`Monster template '${templateName}' not found in MonsterDataLoader`);
    }
    const legacy = monsterDataLoader.toLegacyFormat(monster);
    return {
      id: `monster_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...legacy,
      ...customizations,
      createdAt: new Date().toISOString()
    };
  }
  
  static getSpell(spellName) {
    return spellDatabase[spellName] || null;
  }
  
  // @date_of_change 2025-08-30
  // Enhanced to include full character data availability
  static getAllCharacterTemplates() {
    const fullTemplates = CharacterDataService.getAllCharacterTemplates();
    if (fullTemplates.length > 0) {
      return fullTemplates.map(template => template.key);
    }
    
    // Fallback to legacy templates
    return Object.keys(fullCharacterTemplates);
  }

  /**
   * Get character data by ID - unified method for Dashboard and BattleRoom
   * @date_of_change: 2025-09-06 - Added missing getCharacter method that CharacterSheet.js calls
   * @param {string} characterId - Character ID to load
   * @returns {Object} Character data in format expected by CharacterSheet.js
   */
  static getCharacter(characterId) {
    // Delegate to getCharacterData to ensure consistent data structure
    return DataService.getCharacterData(characterId);
  }

  // @date_of_change 2025-08-30
  // Added missing getCharacterData method that Dashboard requires
  static getCharacterData(characterId) {
    
    // date_of_change: 2025-10-18 - SSOT: Attempt canonical UnifiedDataManager lookup (local cache) before template creation
    try {
      const UnifiedDataManager = require('./UnifiedDataManager').default;
      const canonicalEntity = UnifiedDataManager.loadFromLocalStorage(characterId, 'character');
      if (canonicalEntity && canonicalEntity.type === 'character' && canonicalEntity.data) {
        const canonicalData = canonicalEntity.data;
        const identity = canonicalData.identity || {};
        const primaryClass = Array.isArray(canonicalData.classes) ? canonicalData.classes[0] || {} : {};

        // date_of_change: 2025-10-12 - Normalize canonical ability scores for editor inputs (D&D 2024 ability score table)
        const abilityPairs = [
          ['str', 'strength'],
          ['dex', 'dexterity'],
          ['con', 'constitution'],
          ['int', 'intelligence'],
          ['wis', 'wisdom'],
          ['cha', 'charisma']
        ];
        const normalizedAbilities = {};
        abilityPairs.forEach(([shortKey, longKey]) => {
          const rawAbilities = canonicalData.abilities || {};
          const score = rawAbilities[shortKey] ?? rawAbilities[longKey] ?? null;
          if (score !== undefined) {
            normalizedAbilities[shortKey] = score;
            normalizedAbilities[longKey] = score;
          }
        });

        const hpBlock = canonicalData.hp || canonicalData.hitPoints || {};
        const acBlock = canonicalData.ac || canonicalData.combatStats || {};
        const speedBlock = canonicalData.speed || canonicalData.combatStats || {};
        const inventoryBlock = canonicalData.equipment || canonicalData.inventory || {};

        const hpCurrent = hpBlock.current ?? hpBlock.value ?? null;
        const hpMaximum = hpBlock.maximum ?? hpBlock.max ?? null;
        const hpTemporary = hpBlock.temporary ?? null;

        const normalizedAC = acBlock.value ?? acBlock.armorClass ?? null;
        const normalizedInitiative = canonicalData.initiative ?? acBlock.initiative ?? null;
        const normalizedSpeed = speedBlock.walk ?? speedBlock.speed ?? null;

        const normalizedEquipment = {
          armor: inventoryBlock.armor || null,
          weapons: inventoryBlock.weapons || null,
          items: inventoryBlock.items || null,
          currency: inventoryBlock.currency || null,
          other: inventoryBlock.other || null
        };

        const dataQualityWarnings = [];
        if (!identity.name) {
          dataQualityWarnings.push('Name missing in canonical identity; open Character Editor to set identity.name.');
        }
        if (!primaryClass.name) {
          dataQualityWarnings.push('Primary class missing; add at least one class entry to canonical classes array.');
        }
        if (hpCurrent === null || hpMaximum === null) {
          dataQualityWarnings.push('Hit point block incomplete; ensure hp.current and hp.maximum are defined.');
        }
        if (Object.keys(normalizedAbilities).length === 0) {
          dataQualityWarnings.push('Ability scores unavailable; populate abilities on the character record.');
        }

        const legacyBridge = {
          // date_of_change: 2025-10-12 - Preserve canonical identity fields for CharacterSheetEditor compatibility
          id: canonicalEntity.id,
          name: identity.name ?? canonicalEntity.id,
          class: primaryClass.name ?? null,
          subclass: primaryClass.subclass ?? null,
          level: canonicalData.level ?? primaryClass.level ?? null,
          race: identity.race ?? null,
          species: identity.species ?? null,
          background: identity.background ?? null,
          alignment: identity.alignment ?? null,
          inspiration: canonicalData.inspiration ?? null,
          experience: canonicalData.experiencePoints ?? canonicalData.experience ?? null,

          // Combat stats
          hp: hpCurrent,
          maxHp: hpMaximum,
          tempHp: hpTemporary,
          ac: normalizedAC,
          initiative: normalizedInitiative,
          speed: normalizedSpeed,
          hitDice: primaryClass.hitDice ?? primaryClass.hitDie ?? canonicalData.hitDice ?? null,
          hitDiceSpent: canonicalData.hitDiceSpent ?? null,

          // Ability scores
          abilities: normalizedAbilities,

          // Skills, saves, features, inventory (preserve canonical structures without transformation)
          skills: canonicalData.skills ?? null,
          savingThrows: canonicalData.saves ?? canonicalData.savingThrows ?? null,
          features: canonicalData.features ?? null,
          feats: canonicalData.feats ?? null,
          inventory: canonicalData.inventory ?? null,
          equipment: normalizedEquipment,
          items: canonicalData.items ?? null,
          actions: canonicalData.actions ?? null,
          spellcasting: canonicalData.spellcasting ?? null,
          spells: canonicalData.spells ?? null,
          senses: canonicalData.senses ?? null,
          passivePerception: canonicalData.passivePerception ?? null,
          proficiencies: canonicalData.proficiencies ?? null,
          appearance: canonicalData.appearance ?? null,
          biography: canonicalData.biography ?? null,
          notes: canonicalData.notes ?? null,

          artwork: canonicalEntity._meta?.artwork ?? null,
          tokenAssetKey: canonicalEntity._meta?.artwork ?? null,

          // Preserve canonical payloads for downstream consumers that expect complete data blobs
          data: canonicalData,
          canonicalData,
          isCustom: true,
          source: canonicalEntity.source,
          _meta: canonicalEntity._meta,
          dataQualityWarnings
        };

        return legacyBridge;
      }
    } catch (udmError) {
      // Attempt to load from CustomAssetManager
    }

    // date_of_change: 2025-08-31 - Check CustomAssetManager for custom characters
    if (characterId.startsWith('custom_character_')) {
      try {
        const CustomAssetManager = require('./CustomAssetManager').default;
        const customCharacter = CustomAssetManager.loadCustomAsset(characterId);
        
        if (customCharacter && customCharacter.customData) {
          const customData = customCharacter.customData;
          const transformedData = {
            // Basic character info
            id: customCharacter.id,
            name: customData.name || customCharacter.name,
            class: customData.class || customData.characterClass,
            level: customData.level,
            race: customData.race,
            background: customData.background,
            alignment: customData.alignment,
            
            // Combat stats
            hp: customData.hp || customData.hitPoints?.current,
            maxHp: customData.maxHp || customData.hitPoints?.maximum || customData.hitPoints?.max,
            ac: customData.ac || customData.armorClass,
            
            // Ability scores (preserve both formats for compatibility)
            abilities: customData.abilities,
            // date_of_change: 2025-09-13 - Architectural enforcement: do NOT derive legacy short stats here
            // rule_reference: ZERO TRANSFORMATION RULE — UI/components may derive short-form from abilities if needed
            stats: customData.stats,
            
            // Core game mechanics  
            skills: customData.skills,
            savingThrows: customData.savingThrows,
            spells: customData.spells,
            
            // CRITICAL: Preserve rich data arrays that CharacterSheet.js expects
            classFeatures: customData.classFeatures || [],
            items: customData.items || [],
            feats: customData.feats || [],
            actions: customData.actions,
            features: customData.features || [],
            traits: customData.traits || [],
            
            // Equipment (provide both formats)
            // date_of_change: 2025-09-13 - Keep canonical equipment structure only; no array conversion
            equipment: customData.equipment,
            equipmentStructured: customData.equipment, // Preserve structured format explicitly for clarity
            
            // Character biography and personality details
            appearance: customData.appearance,
            backstory: customData.backstory,
            experiencePoints: customData.experiencePoints,
            personalTraits: customData.personalTraits,
            ideals: customData.ideals,
            bonds: customData.bonds,
            flaws: customData.flaws,
            
            data: {
              name: customData.name || customCharacter.name,
              ...customData
            },
            isCustom: true
          };
          return transformedData;
        }
      } catch (error) {
        // Attempt to create from template
      }
    }
    
    // Fallback to creating from template if it's a template name
    try {
      const fullCharacter = CharacterDataService.createCharacterFromTemplate(characterId);
      if (fullCharacter) {
        // Return character object directly as CharacterDataService provides it
        return fullCharacter;
      }
    } catch (error) {
      // Return null if not found
    }
    
    return null;
  }
  
  static getAllMonsterTemplates() {
    // date_of_change: 2025-10-18 - Enumerate from MonsterDataLoader only (SSOT)
    try {
      const all = monsterDataLoader.getAllMonsters();
      return Object.keys(all || {});
    } catch (_) {
      return [];
    }
  }
  
  static async getMonsterData(monsterId) {
    debugLog(`[DataService] getMonsterData called with monsterId: '${monsterId}' (type: ${typeof monsterId})`);
    
    try {
      // Ensure monsters are loaded first
      if (!monsterDataLoader.isLoaded) {
        debugLog(`[DataService] Loading monsters first...`);
        await monsterDataLoader.loadAllMonsters();
      }
      
      // First try to load from standard monster data
      try {
        const monster = await monsterDataLoader.getMonster(monsterId);
        if (monster) {
          debugLog(`✅ Found standard monster: ${monster.name} (${monsterId})`);
          // Convert to legacy format for backward compatibility
          const legacyData = monsterDataLoader.toLegacyFormat(monster);
          debugLog(`[DataService] Legacy format data:`, legacyData);
          return legacyData;
        }
      } catch (monsterError) {
        debugLog(`[DataService] Failed to load monster ${monsterId} from standard data:`, monsterError);
      }
      
      // If not found in standard data, try custom monsters
      try {
        const { default: CustomAssetManager } = await import('./CustomAssetManager');
        const customAssets = await CustomAssetManager.getAllCustomAssets();
        
        // Normalize the search ID for consistent comparison
        // date_of_change: 2025-09-13 - Lint: simplify character class for hyphen in character class
        const normalizeId = (id) => id ? id.toString().toLowerCase().replace(/[\s_-]/g, '') : '';
        const searchId = normalizeId(monsterId);
        
        debugLog(`🔍 Searching for custom monster matching ID: ${monsterId} (normalized: ${searchId})`);
        
        // Try to find a matching custom monster
        const customMonster = customAssets.monsters.find(m => {
          if (!m) return false;
          
          // Check multiple possible ID fields and name variations
          const match = (
            (m.id && normalizeId(m.id) === searchId) ||
            (m.name && normalizeId(m.name) === searchId) ||
            (m.customData && (
              (m.customData.id && normalizeId(m.customData.id) === searchId) ||
              (m.customData.name && normalizeId(m.customData.name) === searchId)
            ))
          );
          
          if (match) {
            debugLog(`🔍 Potential match found: ${m.name || 'unnamed'} (ID: ${m.id})`);
          }
          
          return match;
        });
        
        if (customMonster) {
          debugLog(`✅ Found custom monster: ${customMonster.name} (original ID: ${monsterId})`);
          debugLog('🔍 Custom monster structure:', customMonster);
          debugLog('🔍 Custom monster customData:', customMonster.customData);
          
          // Comprehensive JSON schema detection and handling
          
          // Case 1: Standard schema with nested data property
          if (customMonster.customData && customMonster.customData.data) {
            debugLog('🔄 Converting JSON schema custom monster to legacy format (data structure)');
            const jsonSchemaData = {
              name: customMonster.customData.data.name,
              data: customMonster.customData.data
            };
            const legacyData = monsterDataLoader.toLegacyFormat(jsonSchemaData);
            debugLog('✅ Converted custom monster legacy data:', legacyData);
            return legacyData;
          } 
          // Case 2: JSON schema stored directly as customData (common with uploads)
          else if (customMonster.customData && (
            customMonster.customData.schemaVersion || 
            customMonster.customData.templateId || 
            customMonster.customData.monster || 
            customMonster.customData.legendary_actions ||
            customMonster.customData.actions
          )) {
            debugLog('🔄 Converting full JSON schema custom monster to legacy format');
            // Backend S3 monster documents are already flat. Passing them only
            // under `data` drops their stable ID and tokenAssetKey in the legacy
            // adapter, so an edit can accidentally save as a second monster.
            const jsonSchemaData = {
              ...customMonster.customData,
              id: customMonster.customData.id || customMonster.id,
              name: customMonster.customData.name || customMonster.name,
            };
            const legacyData = monsterDataLoader.toLegacyFormat(jsonSchemaData);
            debugLog('✅ Converted full JSON schema to legacy data:', legacyData);
            return legacyData;
          } else {
            // Legacy custom monster format with nested customData check
            let monsterData;
            
            // Check for double-nested customData (customMonster.customData.customData)
            if (customMonster.customData && customMonster.customData.customData) {
              debugLog('🔄 Detected double-nested customData structure');
              // Extract the nested customData and use it as the base
              monsterData = {
                ...(customMonster.customData.customData || {}),
                // Ensure core properties from outer structure are preserved
                id: customMonster.id,
                name: customMonster.name,
                isCustom: true,
                // Add other important properties from first level of customData if not in nested data
                cr: customMonster.customData.customData?.cr || customMonster.customData.cr || '1/4',
                hp: customMonster.customData.customData?.hp || customMonster.customData.hp || 10,
                maxHp: customMonster.customData.customData?.maxHp || customMonster.customData.maxHp || 10,
                ac: customMonster.customData.customData?.ac || customMonster.customData.ac || 10
              };
              
              // Note: Removed hardcoded dragon overrides to allow proper data loading from saved monsters
              debugLog('🔄 Using saved monster data without hardcoded overrides');
            } else {
              // Regular structure (no double nesting)
              monsterData = {
                ...(customMonster.customData || {}),
                id: customMonster.id,
                name: customMonster.name,
                isCustom: true
              };
            }
            
            debugLog('📋 Custom monster data (legacy format):', monsterData);
            return monsterData;
          }
        } else {
          debugLog(`ℹ️ No custom monster found matching ID: ${monsterId}`);
          debugLog('Available custom monsters:', customAssets.monsters.map(m => m?.name || 'unnamed'));
        }
      } catch (error) {
        debugLog('Error loading custom monsters:', error);
      }
      
      // If we get here, try falling back to goblin if we haven't already
      if (monsterId !== 'goblin') {
        debugLog(`[DataService] Attempting fallback to goblin...`);
        try {
          const fallbackMonster = await monsterDataLoader.getMonster('goblin');
          if (fallbackMonster) return fallbackMonster;
        } catch (fallbackError) {
          debugLog(`[DataService] Fallback to goblin failed:`, fallbackError);
        }
      }
      
      // If we get here, no monster was found
      debugLog(`[DataService] Monster not found: ${monsterId}`);
      return null;
    } catch (error) {
      debugLog('[DataService] Unexpected error getting monster data:', error);
      return null;
    }
  }
  
  static async getMonsterDataWithSchema(monsterId) {
    try {
      // First try to get from new comprehensive schema monsters (already in correct format)
      return await monsterDataLoader.getMonster(monsterId);
    } catch (error) {
      // Fallback: get legacy monster data and migrate to new schema
      debugLog(`Monster ${monsterId} not found in new schema, migrating legacy...`);
      const legacyMonster = await this.getMonsterData(monsterId);
      return MonsterSchemaUtils.migrateOldMonster(legacyMonster);
    }
  }
  
  static getAllMonsters() {
    // date_of_change: 2025-10-18 - SSOT: return only MonsterDataLoader monsters (legacy projection)
    const newSchemaMonsters = monsterDataLoader.getAllMonsters() || {};
    const allMonsters = {};
    for (const [id, monster] of Object.entries(newSchemaMonsters)) {
      allMonsters[id] = monsterDataLoader.toLegacyFormat(monster);
    }
    return allMonsters;
  }
  
  static getAllMonstersWithSchema() {
    // date_of_change: 2025-10-18 - SSOT: return MonsterDataLoader monsters only (schema-native)
    return { ...(monsterDataLoader.getAllMonsters() || {}) };
  }
  
  static createEmptyMonster() {
    // Create a new empty monster using the comprehensive schema
    return MonsterSchemaUtils.createEmptyMonster();
  }
  
  static validateMonster(monster) {
    // Basic validation - could be enhanced with full JSON schema validation
    const required = ['schemaVersion', 'type', 'id', 'data'];
    const dataRequired = ['name', 'size', 'creatureType', 'alignment', 'cr', 'ac', 'hp', 'speed', 'abilities'];
    
    for (const field of required) {
      if (!monster[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
    
    for (const field of dataRequired) {
      if (!monster.data[field]) {
        throw new Error(`Missing required data field: ${field}`);
      }
    }
    
    return true;
  }
  
  static async exportMonsterWithSchema(monsterId) {
    // Export monster in the new comprehensive schema format
    const schemaMonster = await this.getMonsterDataWithSchema(monsterId);
    return {
      ...schemaMonster,
      exportDate: new Date().toISOString(),
      templateId: monsterId
    };
  }
  
  static rollInitiative(character) {
    const dexMod = Math.floor((character.stats.dex - 10) / 2);
    const roll = Math.floor(Math.random() * 20) + 1;
    return roll + dexMod;
  }
  
  static rollDamage(damageString) {
    // Simple damage roller for dice notation like "1d8+2"
    const match = damageString.match(/(\d+)d(\d+)([+-]\d+)?/);
    if (!match) return 0;
    
    const [, numDice, dieSize, modifier] = match;
    let total = 0;
    
    for (let i = 0; i < parseInt(numDice); i++) {
      total += Math.floor(Math.random() * parseInt(dieSize)) + 1;
    }
    
    if (modifier) {
      total += parseInt(modifier);
    }
    
    return Math.max(0, total);
  }

  // ================================
  // Consolidated Import/Export Methods
  // @date_of_change 2025-09-12 - Added methods to eliminate duplicate services
  // Following AGENTS.md single source of truth architecture
  // ================================

  /**
   * Import character using single source of truth architecture
   * GOVERNANCE RULE: Canonical JSON → UnifiedDataManager (single source of truth)
   * date_of_change: 2025-09-16 - Replace multi-normalizer flow with clean canonical ingest
   */
  static async importCharacter(rawCharacterData) {
    debugLog('[DataService] Importing character - Single source of truth');
    try {
      // date_of_change: 2025-10-17 - Switch to UnifiedDataNormalizer → Canonical → UnifiedDataManager
      const { default: UnifiedDataManager } = await import('./UnifiedDataManager');
      const { default: UnifiedDataNormalizer } = await import('../normalizers/UnifiedDataNormalizer');

      const { canonical, audit } = await UnifiedDataNormalizer.normalize(rawCharacterData, 'character');
      const now = new Date().toISOString();
      const entity = {
        schemaVersion: '1.0.0',
        type: 'character',
        exportDate: now,
        id: canonical.id,
        source: { name: 'Upload', edition: '5e', license: 'Custom' },
        data: canonical,
        _meta: { createdAt: now, lastModified: now, audit }
      };

      const saved = await UnifiedDataManager.saveEntity(entity, true);
      debugLog('[DataService] Character stored via UnifiedDataManager (canonical):', saved.id);

      return {
        id: saved.id,
        name: saved.data?.name,
        level: saved.data?.level,
        class: saved.data?.class,
        _metadata: { confidence: 1, warnings: [] }
      };

    } catch (error) {
      debugLog('[DataService] importCharacter failed:', error);
      throw error;
    }
  }

  /**
   * Import monster using single source of truth architecture  
   * GOVERNANCE RULE: MonsterDataNormalizer → Canonical → Storage
   */
  static async importMonster(rawMonsterData) {
    debugLog('[DataService] Importing monster - Single source of truth (MonsterEntityAdapter → UDM)');

    try {
      // date_of_change: 2025-12-03
      // rule_reference: AGENTS.md SSOT — Delegate monster ingest to ImportExportService.importMonster
      // so all persisted monsters share the MonsterEntityAdapter-based canonicalization pipeline.
      const { default: ImportExportService } = await import('./ImportExportService');
      const entity = await ImportExportService.importMonster(rawMonsterData);

      return {
        ...entity.data,
        id: entity.id,
        _metadata: {
          confidence: 1.0,
          warnings: [],
          normalizer: 'MonsterEntityAdapter',
          source: 'DataService.importMonster'
        }
      };
    } catch (error) {
      debugLog('[DataService] importMonster failed:', error);
      throw error;
    }
  }

  /**
   * Async version of getCharacter for compatibility with async callers
   */
  static async getCharacterAsync(characterId) {
    // Delegate to sync version for now - can be made fully async later if needed
    return this.getCharacter(characterId);
  }

  /**
   * Async version of getMonster for compatibility with async callers
   */
  static async getMonsterAsync(monsterId) {
    // Delegate to existing getMonsterData method
    return this.getMonsterData(monsterId);
  }

  /**
   * Validate character data against canonical schema
   */
  static async validateCharacterData(character) {
    try {
      // Basic validation - can be extended with more sophisticated schema validation
      const required = ['name', 'level', 'class', 'abilities'];
      const missing = required.filter(field => !character[field]);
      
      if (missing.length > 0) {
        return {
          valid: false,
          errors: missing.map(field => `Missing required field: ${field}`)
        };
      }
      
      return {
        valid: true,
        errors: []
      };
      
    } catch (error) {
      return {
        valid: false,
        errors: [`Validation error: ${error.message}`]
      };
    }
  }
}

export default DataService;
